import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import {
  createAiShortCircuitError,
  isAiRequestBlocked,
  noteAiFailure,
  noteAiSuccess,
  resolveAiContourFamily,
} from './aiResilience';

// Single API base URL (Vite proxy or gateway); per-service URLs are deprecated.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
const REFRESH_TIMEOUT_MS = 10000;

export function isPublicApiPath(url?: string | null): boolean {
  if (!url) return false;
  const check = (raw: string): boolean => {
    const normalized = raw.startsWith('/') ? raw : `/${raw}`;
    // FLOW[44043]: «public»-роуты engagement-ai (превью-подсказки) исторически
    // назывались public, но несут бизнес-данные и после BLK-05 закрыты
    // периметром — на них Bearer ОБЯЗАТЕЛЕН. Анонимными остаются только
    // настоящие публичные страницы (КП/proof по токен-ссылке).
    if (normalized.includes('/engagement-ai/')) return false;
    return normalized.startsWith('/public/') || normalized.includes('/public/');
  };
  try {
    const parsed = url.startsWith('http') ? new URL(url) : null;
    return check(parsed ? parsed.pathname : url);
  } catch {
    return check(url);
  }
}

export function shouldBypassAuthRefresh(url?: string | null): boolean {
  return (
    isPublicApiPath(url) ||
    isIntegrationAuthPath(url) ||
    isSoftSessionBootstrapPath(url)
  );
}

function normalizeApiPath(url?: string | null): string {
  if (!url) return '';
  try {
    const parsed = url.startsWith('http') ? new URL(url) : null;
    const path = parsed ? parsed.pathname : url;
    return path.startsWith('/') ? path : `/${path}`;
  } catch {
    return url.startsWith('/') ? url : `/${url}`;
  }
}

function isIntegrationAuthPath(url?: string | null): boolean {
  const normalized = normalizeApiPath(url);
  return normalized.includes('/1c-fresh/');
}

export function isSoftSessionBootstrapPath(url?: string | null): boolean {
  const normalized = normalizeApiPath(url);
  return normalized.includes('/prepress/public-session');
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

type AuthTokenPair = {
  access_token: string;
  refresh_token: string;
};

const normalizeStoredToken = (token: string | null | undefined): string | null => {
  const trimmedToken = token?.trim();
  if (!trimmedToken || trimmedToken === 'undefined' || trimmedToken === 'null') {
    return null;
  }
  return trimmedToken;
};

const readStoredToken = (key: string): string | null => {
  const token = localStorage.getItem(key);
  const normalizedToken = normalizeStoredToken(token);
  if (token && !normalizedToken) {
    localStorage.removeItem(key);
  }
  return normalizedToken;
};

function toAuthTokenPair(value: unknown): AuthTokenPair | null {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Partial<Record<keyof AuthTokenPair, unknown>>;
  if (
    typeof candidate.access_token !== 'string' ||
    typeof candidate.refresh_token !== 'string'
  ) {
    return null;
  }

  const accessToken = normalizeStoredToken(candidate.access_token);
  const refreshToken = normalizeStoredToken(candidate.refresh_token);
  if (!accessToken || !refreshToken) {
    return null;
  }

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
  };
}

export function extractRefreshTokenPair(responseData: unknown): AuthTokenPair {
  const directTokenPair = toAuthTokenPair(responseData);
  if (directTokenPair) {
    return directTokenPair;
  }

  if (responseData && typeof responseData === 'object') {
    const maybeEnvelope = responseData as { data?: unknown };
    const envelopedTokenPair = toAuthTokenPair(maybeEnvelope.data);
    if (envelopedTokenPair) {
      return envelopedTokenPair;
    }
  }

  throw new Error('Invalid token refresh response');
}

export const tokenStorage = {
  getAccessToken: () => readStoredToken(TOKEN_KEY),
  getRefreshToken: () => readStoredToken(REFRESH_TOKEN_KEY),
  setTokens: (accessToken: string, refreshToken: string) => {
    const normalizedAccessToken = normalizeStoredToken(accessToken);
    const normalizedRefreshToken = normalizeStoredToken(refreshToken);

    if (!normalizedAccessToken || !normalizedRefreshToken) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      throw new Error('Refusing to store invalid auth tokens');
    }

    localStorage.setItem(TOKEN_KEY, normalizedAccessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, normalizedRefreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export const refreshTokens = (refreshToken: string) =>
  axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    { timeout: REFRESH_TIMEOUT_MS }
  );

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // FLOW[47471]: Circuit breaker — если AI-контур помечен недоступным (404/501) или в окне
    // бэкоффа (5xx/сеть), гасим запрос ЗДЕСЬ, не выходя в сеть. Это останавливает шторм
    // повторных запросов и лог-спам независимо от того, какой вызывающий зациклился.
    const blockedFamily = isAiRequestBlocked(config.url)
      ? resolveAiContourFamily(config.url)
      : null;
    if (blockedFamily) {
      return Promise.reject(createAiShortCircuitError(blockedFamily, config));
    }
    const token = tokenStorage.getAccessToken();
    if (token && config.headers && !isPublicApiPath(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData && config.headers) {
      const headers = config.headers as Record<string, string>;
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    // FLOW[47472]: успешный ответ AI-контура закрывает circuit (сброс в ok).
    noteAiSuccess(response.config?.url);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = originalRequest?.url;

    // FLOW[47472]: регистрируем сбой AI-контура (404/501 → unavailable; 5xx/сеть → degraded).
    // Синтетические короткозамкнутые ошибки игнорируются внутри noteAiFailure.
    noteAiFailure(requestUrl, error);

    // 1C integration auth errors are upstream-business errors, not ERP session errors.
    // Do not trigger token refresh/logout flow for these requests.
    if (error.response?.status === 401 && shouldBypassAuthRefresh(requestUrl)) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(apiClient(originalRequest));
            },
            reject: (err: AxiosError) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        tokenStorage.clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // FLOW[23332]: Send snake_case to backend (Python convention)
        const response = await refreshTokens(refreshToken);

        // FLOW[23333]: Receive snake_case from backend inside BaseResponse.data.
        const { access_token, refresh_token: newRefreshToken } = extractRefreshTokenPair(
          response.data
        );
        tokenStorage.setTokens(access_token, newRefreshToken);

        processQueue(null, access_token);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        tokenStorage.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
